import { useState } from 'react';
import { Alert, Button, Form, Input } from 'antd';
import { useTranslation } from 'react-i18next';
import { EntryLayout } from './EntryLayout';
import { usePortalConfig } from '../config/PortalConfigProvider';
import { useSession } from '../session/SessionProvider';
import { errorText } from '../session/errorText';
import type { Navigation } from '../router';

/**
 * The login screen (`ui-enduser-personal-area` §1; Appendix L §4 F1).
 *
 * # The credential
 *
 * `subscriber.login` + `subscriber.password` — the SUBSCRIBER credential (customer
 * decision **P7-A1**). The Account credential the enrollment portal asks for is a
 * DIFFERENT thing, and the operator has to live with a subscriber holding two, so the
 * field carries the one-sentence hint the UI spec's own field note asks for. That hint
 * is the only mention of the other credential on this screen: no link to the enrollment
 * portal, no explanation of when to use which, nothing a phone call is better at.
 *
 * # The error
 *
 * WHATEVER THE SERVER SAID, verbatim (`errorText`). The route answers one flat 401 for
 * six causes and the reason is in `handlers.go`: a distinguishable refusal is a
 * subscriber-enumeration oracle over logins derived from contract numbers, against a
 * password this system stores recoverably by design. So this screen adds no distinction
 * of its own — not «логин не найден», not «пароль неверен», and it does not count
 * attempts or hint at a lockout (the 429 has its own server message about the CALLER's
 * address, which is safe to be distinguishable and is shown the same way).
 */
export function LoginScreen({ navigate }: { navigate: Navigation['navigate'] }) {
  const { t } = useTranslation();
  const { signIn } = useSession();
  const config = usePortalConfig();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const submit = async (values: { login: string; password: string }) => {
    setBusy(true);
    setError(null);
    try {
      await signIn(values.login, values.password);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <EntryLayout>
      <h2 className="pa-page-title">{t('login.title')}</h2>
      <p className="pa-lead">{t('login.lead')}</p>
      {/* THE OPERATOR'S OWN WELCOME TEXT (`GET /pa/config`), rendered as the plain string
          it is — the registry stores it as text and warns the operator that it is shown to
          an unauthenticated visitor. It is not Markdown and not HTML: a login screen is
          not a place to render authored markup, and the one surface that does
          («Другие способы оплаты») has a renderer built for it. */}
      {config.login_welcome_text ? (
        <p className="pa-lead">{config.login_welcome_text}</p>
      ) : null}

      {error ? (
        <Alert type="error" showIcon message={errorText(error, t)} style={{ marginBottom: 20 }} />
      ) : null}

      <Form layout="vertical" requiredMark={false} onFinish={submit} disabled={busy}>
        <Form.Item
          name="login"
          label={t('login.loginLabel')}
          extra={t('login.loginHint')}
          rules={[{ required: true, message: t('common.required') }]}
        >
          <Input
            placeholder={t('login.loginPlaceholder')}
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            size="large"
          />
        </Form.Item>

        <Form.Item
          name="password"
          label={t('login.passwordLabel')}
          rules={[{ required: true, message: t('common.required') }]}
        >
          {/* No charset rule here: C18 binds a NEW password only, and a login form must
              accept whatever is stored or the Phase-8 import locks out every subscriber
              whose legacy password holds a character C18 forbids (P7-defaults). */}
          <Input.Password
            placeholder={t('login.passwordPlaceholder')}
            autoComplete="current-password"
            size="large"
          />
        </Form.Item>

        {/* One clear primary action per screen (P7-defaults), full width for a thumb. */}
        <Button type="primary" htmlType="submit" size="large" block loading={busy}>
          {t('login.submit')}
        </Button>
      </Form>

      <div className="pa-form-footer">
        <Button type="link" onClick={() => navigate({ kind: 'recover' })} style={{ padding: 0 }}>
          {t('login.forgot')}
        </Button>
        {/* ⚠ THE DESTINATION IS THE OPERATOR'S AND MAY BE A THIRD-PARTY DOMAIN, which will
            not open for a subscriber suspended for non-payment: the walled garden learns
            only the acquirer's own domains. The registry entry says so to the operator;
            the portal renders what he wrote rather than second-guessing it, and the link
            is absent entirely when he has written nothing. */}
        {config.terms_url ? (
          <a href={config.terms_url} target="_blank" rel="noopener noreferrer">
            {t('login.terms')}
          </a>
        ) : null}
      </div>
    </EntryLayout>
  );
}
